import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useUserStore } from '../stores/userStore';
import { useLanguage } from '../hooks/useLanguage';
import { tasksApi, userApi } from '../lib/api';
import { TaskCard } from '../components/TaskCard';
import { ProgressCard } from '../components/ProgressCard';

export function HomePage() {
  const { user, stats } = useUserStore();
  const { t } = useLanguage();

  const { data: tasks, isLoading } = useQuery({
    queryKey: ['tasks', 'featured'],
    queryFn: () => tasksApi.list({ limit: 5 }),
  });

  const { data: submissions } = useQuery({
    queryKey: ['submissions', user?.telegram_id],
    queryFn: () => userApi.getSubmissions(user!.telegram_id),
    enabled: !!user?.telegram_id,
  });

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="bg-tg rounded-xl p-5">
        <h1 className="text-xl font-bold text-tg">
          {t('welcome')}, {user?.first_name || 'Guest'}
        </h1>
        <p className="text-tg-hint mt-1">
          {t('welcome_desc')}
        </p>
      </div>

      {/* Progress Tracking */}
      {stats && (
        <ProgressCard
          balanceUsd={stats.balance_usd}
          totalEarnedUsd={stats.total_earned_usd}
          tasksCompleted={stats.tasks_completed}
          pendingSubmissions={(submissions as any[]) || []}
          minPayout={500}
        />
      )}

      {/* Featured Tasks */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-tg">{t('available_tasks')}</h2>
          <Link to="/tasks" className="text-sm text-tg-link">
            {t('view_all')}
          </Link>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-tg rounded-xl p-4 animate-pulse">
                <div className="flex gap-3">
                  <div className="w-12 h-12 bg-tg-secondary rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-tg-secondary rounded w-3/4" />
                    <div className="h-3 bg-tg-secondary rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {(tasks as any[])?.map((task: any) => (
              <TaskCard key={task.id} task={task} />
            ))}
          </div>
        )}
      </section>

      {/* Quick Actions */}
      <section className="grid grid-cols-2 gap-3">
        <Link
          to="/wallet"
          className="bg-tg rounded-xl p-4 text-center"
        >
          <div className="text-2xl mb-2 font-semibold text-tg">{t('wallet')}</div>
          <div className="text-sm text-tg-hint">{t('wallet_desc')}</div>
        </Link>
        <Link
          to="/leaderboard"
          className="bg-tg rounded-xl p-4 text-center"
        >
          <div className="text-2xl mb-2 font-semibold text-tg">{t('leaderboard')}</div>
          <div className="text-sm text-tg-hint">{t('leaderboard_desc')}</div>
        </Link>
      </section>
    </div>
  );
}
